import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Card, Loading, EmptyState } from "../components/UI";
import { formatApiError, statusBadgeClass, statusLabel, formatDate, roleLabel } from "../lib/format";
import { useAuth } from "../lib/auth";
import { UserPlus, X, Trash2, Mail, Phone, ShieldCheck, Send, Search, RotateCcw, UserCog, ArrowUpRight, Link as LinkIcon, AlertTriangle, Home, FileSignature, Inbox, ScrollText } from "lucide-react";

const STAFF_ROLES = [
  { v: "admin", l: "Admin / Landlord" },
  { v: "manager", l: "Property Manager" },
  { v: "lawyer", l: "Legal Officer" },
  { v: "maintenance", l: "Maintenance Staff" },
];

const TABS = [
  { k: "all", l: "All", filter: () => true },
  { k: "admin", l: "Admins", filter: (u) => u.role === "admin" },
  { k: "manager", l: "Managers", filter: (u) => u.role === "manager" },
  { k: "lawyer", l: "Legal", filter: (u) => u.role === "lawyer" },
  { k: "maintenance", l: "Maintenance", filter: (u) => u.role === "maintenance" },
  { k: "tenant", l: "Tenants", filter: (u) => u.role === "tenant" },
];

const ROLE_BADGE = {
  admin: "bg-[#7A6810] text-white",
  manager: "bg-[#0E5C3A] text-white",
  lawyer: "bg-[#5D4E75] text-white",
  maintenance: "bg-[#8B6F47] text-white",
  tenant: "bg-[#E5EBE8] text-[#1A2E24]",
};

export default function Staff() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [tempPwd, setTempPwd] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [roleChange, setRoleChange] = useState(null); // { user, newRole }
  const [linked, setLinked] = useState(null); // { user, data, loading } — view related records
  const [hardDelete, setHardDelete] = useState(null); // { user, reason, typed }

  const reload = async () => {
    const r = await api.get("/users");
    setUsers(r.data);
    setLoading(false);
  };
  useEffect(() => { reload(); }, []);

  const filtered = useMemo(() => {
    const tabFn = TABS.find((t) => t.k === tab)?.filter || (() => true);
    const term = q.trim().toLowerCase();
    return users
      .filter(tabFn)
      .filter((u) => !term || (u.full_name || "").toLowerCase().includes(term) || (u.email || "").toLowerCase().includes(term) || (u.phone || "").includes(term));
  }, [users, tab, q]);

  const counts = useMemo(() => {
    const c = {};
    TABS.forEach((t) => { c[t.k] = users.filter(t.filter).length; });
    return c;
  }, [users]);

  const submit = async (e) => {
    e.preventDefault();
    const f = new FormData(e.target);
    try {
      const { data } = await api.post("/auth/invite-staff", {
        full_name: f.get("full_name"),
        email: f.get("email"),
        phone: f.get("phone"),
        role: f.get("role"),
      });
      setTempPwd({ email: f.get("email"), password: data.temp_password, role: data.role });
      setCreating(false);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const disable = async (u) => {
    if (u.id === me.id) { toast.error("You cannot disable your own account"); return; }
    if (!confirm(`Disable ${u.full_name}? They will lose access immediately.`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Disabled"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const enable = async (u) => {
    try { await api.patch(`/users/${u.id}`, { status: "active" }); toast.success("Enabled"); reload(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const resend = async (u) => {
    const label = u.role === "tenant" ? "Reset password" : "Resend invite";
    if (!confirm(`${label} for ${u.full_name}? A new temporary password will replace the old one.`)) return;
    try {
      const { data } = await api.post(`/users/${u.id}/resend-invite`);
      setTempPwd({ email: u.email, password: data.temp_password, role: data.role });
      toast.success(data.email_sent ? `${label} email sent` : `Temp password regenerated (email failed — share manually)`);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyRoleChange = async () => {
    if (!roleChange) return;
    if (roleChange.user.id === me.id && roleChange.newRole !== "admin") {
      toast.error("You cannot demote your own admin account. Ask another admin to do this.");
      return;
    }
    try {
      await api.patch(`/users/${roleChange.user.id}`, { role: roleChange.newRole });
      toast.success(`Role changed to ${roleLabel(roleChange.newRole)}`);
      setRoleChange(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openLinked = async (u) => {
    setLinked({ user: u, data: null, loading: true });
    try {
      const { data } = await api.get(`/users/${u.id}/related`);
      setLinked({ user: u, data, loading: false });
    } catch (err) {
      toast.error(formatApiError(err));
      setLinked(null);
    }
  };

  const submitHardDelete = async () => {
    if (!hardDelete) return;
    const { user: u, reason, typed } = hardDelete;
    if (typed !== "DELETE") { toast.error('Type "DELETE" exactly to confirm'); return; }
    if ((reason || "").trim().length < 10) { toast.error("Reason must be at least 10 characters"); return; }
    try {
      const { data } = await api.delete(`/users/${u.id}?force=true`, { data: { reason } });
      toast.success(
        data.tenants_unlinked
          ? `${u.full_name} permanently removed. Tenant record kept but un-linked.`
          : `${u.full_name} permanently removed from users table.`
      );
      setHardDelete(null);
      reload();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const isAdmin = me?.role === "admin";

  if (loading) return <Loading/>;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3 fade-up">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A2E24]">User Access & Staff</h1>
          <p className="text-[#5E7166] text-sm">Everyone with a login to Modav Homes — {users.length} accounts total. Admins can change roles, reset passwords, disable and re-invite.</p>
        </div>
        {isAdmin && (
          <button onClick={() => setCreating(true)} className="btn-gold" data-testid="invite-staff-btn">
            <UserPlus size={16}/> Invite Staff
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2" data-testid="user-tabs">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-colors ${
              tab === t.k
                ? "bg-[#0E5C3A] text-white border-[#0E5C3A]"
                : "bg-white text-[#5E7166] border-[#E5EBE8] hover:bg-[#F7F9F8]"
            }`}
            data-testid={`tab-${t.k}`}
          >
            {t.l} <span className="opacity-70 ml-1">({counts[t.k] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5E7166] pointer-events-none"/>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by name, email or phone…"
          className="input-modav pl-9"
          data-testid="user-search"
        />
      </div>

      <Card className="!p-0 overflow-hidden">
        {filtered.length === 0 ? <EmptyState icon={ShieldCheck} title="No users match" description="Try a different tab or clear the search."/> :
          <div className="overflow-x-auto">
            <table className="modav-table" data-testid="users-table">
              <thead><tr><th>Name</th><th>Role</th><th>Contact</th><th>Status</th><th>Added</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {filtered.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td className="font-semibold">
                      {u.full_name}
                      {u.id === me?.id && <span className="ml-2 text-[10px] text-[#C9A227] uppercase tracking-wider font-bold">(you)</span>}
                    </td>
                    <td>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[u.role] || "bg-gray-200"}`}>
                        {roleLabel(u.role)}
                      </span>
                    </td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1 text-[#5E7166]"><Mail size={11}/>{u.email}</div>
                      {u.phone && <div className="flex items-center gap-1 text-[#5E7166] mt-0.5"><Phone size={11}/>{u.phone}</div>}
                    </td>
                    <td><span className={statusBadgeClass(u.status === "active" ? "active" : "disabled")}>{statusLabel(u.status || "active")}</span></td>
                    <td className="text-xs text-[#5E7166]">{formatDate(u.created_at)}</td>
                    <td>
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openLinked(u)}
                          title="View linked tenant, leases, payments, leads & audit"
                          className="text-[#5E7166] hover:bg-[#F0F3F1] p-2 rounded"
                          data-testid={`linked-${u.id}`}
                        >
                          <LinkIcon size={14}/>
                        </button>
                        {u.status !== "disabled" && (
                          <button
                            onClick={() => resend(u)}
                            title={u.role === "tenant" ? "Reset password" : "Resend invite / new temp password"}
                            className="text-[#0E5C3A] hover:bg-[#E8F5EE] p-2 rounded"
                            data-testid={`resend-invite-${u.id}`}
                          >
                            {u.role === "tenant" ? <RotateCcw size={14}/> : <Send size={14}/>}
                          </button>
                        )}
                        {isAdmin && u.role !== "tenant" && (
                          <button
                            onClick={() => setRoleChange({ user: u, newRole: u.role })}
                            title="Change role"
                            className="text-[#5D4E75] hover:bg-[#EFECF5] p-2 rounded"
                            data-testid={`change-role-${u.id}`}
                          >
                            <UserCog size={14}/>
                          </button>
                        )}
                        {u.status === "disabled" ? (
                          <button
                            onClick={() => enable(u)}
                            title="Re-enable account"
                            className="text-[#0E5C3A] hover:bg-[#E8F5EE] p-2 rounded"
                            data-testid={`enable-${u.id}`}
                          >
                            <ArrowUpRight size={14}/>
                          </button>
                        ) : (
                          u.id !== me?.id && isAdmin && (
                            <button
                              onClick={() => disable(u)}
                              title="Disable account (soft — preserves history)"
                              className="text-[#C92A2A] hover:bg-[#FEF0F0] p-2 rounded"
                              data-testid={`disable-${u.id}`}
                            >
                              <Trash2 size={14}/>
                            </button>
                          )
                        )}
                        {isAdmin && u.id !== me?.id && (
                          <button
                            onClick={() => setHardDelete({ user: u, reason: "", typed: "" })}
                            title="Permanent delete — removes the row (frees the email). Audit-logged, irreversible."
                            className="text-[#7A2020] hover:bg-[#FEF0F0] p-2 rounded"
                            data-testid={`hard-delete-${u.id}`}
                          >
                            <AlertTriangle size={14}/>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </Card>

      {creating && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md" data-testid="invite-staff-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Invite Staff Member</h3>
              <button onClick={() => setCreating(false)}><X size={20}/></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Full Name</span>
                <input name="full_name" required className="input-modav mt-1" data-testid="staff-name"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Email</span>
                <input name="email" type="email" required className="input-modav mt-1" data-testid="staff-email"/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Phone (optional)</span>
                <input name="phone" className="input-modav mt-1" placeholder="+234..."/>
              </label>
              <label className="block">
                <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">Role</span>
                <select name="role" required className="input-modav mt-1" data-testid="staff-role">
                  {STAFF_ROLES.filter((r) => r.v !== "admin" || isAdmin).map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
                </select>
              </label>
              <div className="bg-[#FDF6E3]/40 p-3 rounded-lg text-xs text-[#A0801C]">
                A temporary password will be generated and emailed to this person along with their login link. They'll be prompted to change it on first sign-in.
              </div>
              <button type="submit" className="btn-gold w-full justify-center" data-testid="confirm-invite-staff">Create & Send Invite</button>
            </form>
          </div>
        </div>
      )}

      {roleChange && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setRoleChange(null)}>
          <div className="card-modav p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="role-change-modal">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-serif text-xl font-bold">Change Role</h3>
              <button onClick={() => setRoleChange(null)}><X size={18}/></button>
            </div>
            <p className="text-sm text-[#5E7166] mb-3">Change the role of <b>{roleChange.user.full_name}</b>. Their permissions across the app update immediately. Audit-logged.</p>
            <label className="block mb-4">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">New Role</span>
              <select
                value={roleChange.newRole}
                onChange={(e) => setRoleChange({ ...roleChange, newRole: e.target.value })}
                className="input-modav mt-1"
                data-testid="role-change-select"
              >
                {STAFF_ROLES.map((r) => (
                  <option key={r.v} value={r.v}>{r.l}</option>
                ))}
              </select>
            </label>
            {roleChange.newRole !== roleChange.user.role && roleChange.newRole === "admin" && (
              <div className="bg-[#FEF0F0] border border-[#C92A2A]/30 rounded-lg p-3 text-xs text-[#C92A2A] mb-4">
                ⚠ Promoting to Admin grants FULL access including deleting leases, resetting passwords, and importing data. Only do this for people you fully trust.
              </div>
            )}
            <div className="flex gap-2">
              <button onClick={() => setRoleChange(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button
                onClick={applyRoleChange}
                disabled={roleChange.newRole === roleChange.user.role}
                className="btn-gold flex-1 justify-center"
                data-testid="role-change-confirm"
              >
                Apply Change
              </button>
            </div>
          </div>
        </div>
      )}

      {linked && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setLinked(null)}>
          <div className="card-modav p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="linked-modal">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="font-serif text-xl font-bold">Linked Records</h3>
                <p className="text-xs text-[#5E7166] mt-0.5">{linked.user.full_name} · {linked.user.email}</p>
              </div>
              <button onClick={() => setLinked(null)}><X size={20}/></button>
            </div>
            {linked.loading ? (
              <div className="py-8 text-center text-[#5E7166] text-sm">Loading…</div>
            ) : (
              <div className="space-y-4 text-sm">
                <StatRow icon={UserCog} label="Tenant profile" value={(linked.data?.tenant?.full_name) || <em className="text-[#5E7166]">Not linked</em>}/>
                <StatRow icon={FileSignature} label="Leases" value={`${linked.data?.leases?.length || 0}`}/>
                <StatRow icon={Home} label="Units occupied" value={`${linked.data?.units_occupied?.length || 0}`}/>
                <StatRow icon={RotateCcw} label="Payments" value={`${linked.data?.payments_count || 0}`}/>
                <StatRow icon={Inbox} label="Public enquiries" value={`${linked.data?.leads?.length || 0}`}/>
                <StatRow icon={ScrollText} label="Audit events" value={`${linked.data?.audit_events_count || 0}`}/>

                {linked.data?.leases?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#5E7166] mt-4 mb-2">Leases</div>
                    <div className="space-y-1">
                      {linked.data.leases.map(l => (
                        <div key={l.id} className="flex justify-between text-xs bg-[#F7F9F8] px-3 py-2 rounded">
                          <span className="font-mono">{l.id.slice(0,8)}</span>
                          <span className="capitalize">{(l.lease_status || "").replace(/_/g, " ")}</span>
                          <span className="text-[#5E7166]">{l.lease_start_date} → {l.lease_end_date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {linked.data?.units_occupied?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#5E7166] mt-4 mb-2">Units</div>
                    <div className="space-y-1">
                      {linked.data.units_occupied.map(un => (
                        <div key={un.id} className="flex justify-between text-xs bg-[#F7F9F8] px-3 py-2 rounded">
                          <span>{un.unit_name}</span>
                          <span className="text-[#5E7166]">{un.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {linked.data?.leads?.length > 0 && (
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider text-[#5E7166] mt-4 mb-2">Public Enquiries</div>
                    <div className="space-y-1">
                      {linked.data.leads.map(ld => (
                        <div key={ld.id} className="flex justify-between text-xs bg-[#F7F9F8] px-3 py-2 rounded">
                          <span>{ld.name || "Anonymous"}</span>
                          <span>{ld.unit_type_interest || "—"}</span>
                          <span className="text-[#5E7166]">{ld.status}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {hardDelete && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setHardDelete(null)}>
          <div className="card-modav p-6 w-full max-w-lg border-t-4 border-[#7A2020]" onClick={(e) => e.stopPropagation()} data-testid="hard-delete-modal">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 shrink-0 rounded-full bg-[#FEF0F0] text-[#7A2020] flex items-center justify-center">
                <AlertTriangle size={20}/>
              </div>
              <div className="flex-1">
                <h3 className="font-serif text-xl font-bold text-[#7A2020]">Permanent Delete</h3>
                <p className="text-xs text-[#5E7166] mt-1">Permanently remove <b>{hardDelete.user.full_name}</b> ({hardDelete.user.email}) from the users table.</p>
              </div>
            </div>

            <div className="bg-[#FEF0F0] border-2 border-[#7A2020] rounded-lg p-4 mb-4">
              <div className="text-sm text-[#7A2020] font-bold uppercase tracking-wider mb-2">⚠ Irreversible</div>
              <ul className="text-xs text-[#7A2020] space-y-1 list-disc list-inside">
                <li>The <b>users</b> row is deleted. The email becomes reusable — useful for freeing up a stale account so an existing tenant can reuse that email.</li>
                <li>Linked <b>tenant</b> records are kept but un-linked (user_id set to null). Tenant profile, leases and payments remain untouched.</li>
                <li>Audit logs and historical records are preserved (they store the user_id as a string).</li>
                <li><b>Use "Disable" instead</b> if you only want to block login but keep everything intact.</li>
              </ul>
            </div>

            <label className="block mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">
                Reason <span className="text-[#7A2020]">(required, min 10 chars — audit-logged)</span>
              </span>
              <textarea
                value={hardDelete.reason}
                onChange={(e) => setHardDelete({ ...hardDelete, reason: e.target.value })}
                rows={2}
                placeholder="e.g. Stale orphan record blocking email update for tenant Otuyemi Adedoja"
                className="input-modav mt-1 resize-none"
                data-testid="hard-delete-reason"
              />
            </label>

            <label className="block mb-3">
              <span className="text-xs font-bold uppercase tracking-wider text-[#5E7166]">
                Type <code className="bg-[#FEF0F0] text-[#7A2020] font-bold px-1">DELETE</code> to confirm
              </span>
              <input
                type="text"
                value={hardDelete.typed}
                onChange={(e) => setHardDelete({ ...hardDelete, typed: e.target.value })}
                autoComplete="off"
                className="input-modav mt-1 font-mono"
                data-testid="hard-delete-typed"
              />
            </label>

            <div className="flex gap-2 pt-1">
              <button onClick={() => setHardDelete(null)} className="btn-ghost flex-1 justify-center">Cancel</button>
              <button
                onClick={submitHardDelete}
                disabled={hardDelete.typed !== "DELETE" || (hardDelete.reason || "").trim().length < 10}
                className="flex-1 justify-center inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#7A2020] text-white hover:bg-[#5C1717] disabled:bg-[#5E7166] disabled:cursor-not-allowed text-sm font-bold"
                data-testid="hard-delete-confirm"
              >
                <AlertTriangle size={14}/> Permanently Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {tempPwd && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="card-modav p-6 w-full max-w-md text-center" data-testid="staff-temp-modal">
            <div className="w-14 h-14 mx-auto rounded-full bg-[#E8F5EE] text-[#0E5C3A] flex items-center justify-center mb-3"><ShieldCheck size={24}/></div>
            <h3 className="font-serif text-xl font-bold">Credentials issued</h3>
            <p className="text-[#5E7166] mt-1 text-sm">A welcome email was sent. You can also share these credentials directly (e.g. WhatsApp):</p>
            <div className="bg-[#F7F9F8] p-4 rounded-lg my-4 text-left font-mono text-sm">
              <div><b>Role:</b> {roleLabel(tempPwd.role)}</div>
              <div><b>Email:</b> {tempPwd.email}</div>
              <div className="mt-1"><b>Temp password:</b> <span className="bg-[#FDF6E3] px-1 select-all">{tempPwd.password}</span></div>
            </div>
            <button onClick={() => setTempPwd(null)} className="btn-gold mx-auto" data-testid="temp-pwd-done">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[#E5EBE8] last:border-0">
      <div className="flex items-center gap-2 text-[#5E7166] text-sm">
        <Icon size={14}/> {label}
      </div>
      <div className="font-semibold text-sm text-[#1A2E24]">{value}</div>
    </div>
  );
}

